import { Link, useParams } from 'react-router-dom';
import { ArrowUpRight, ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { codeToSlug, slugToCode } from '../data/products';
import GoldRule from '../components/GoldRule';
import ImageGallery from '../components/ImageGallery';
import { FeatureBadge } from '../components/FeatureBadge';
import useProductImages from '../hooks/useProductImages';
import useProducts from '../hooks/useProducts';

export default function ProductDetail() {
  const { slug } = useParams();
  const { manifest } = useProductImages();
  const { visibleProducts: products, sets } = useProducts();
  const [confirm, setConfirm] = useState(false);

  // Resolve product from merged map (static + custom)
  const code = slugToCode(slug) || slug.toUpperCase().replace(/-/g, ' ');
  const product = products[code];

  if (!product) {
    return (
      <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-32 text-center" data-testid="product-not-found">
        <div className="eyebrow text-mb-muted mb-5">Not found</div>
        <h1 className="font-display italic text-4xl text-mb">The piece you want is not in the atelier.</h1>
        <div className="mt-10">
          <Link to="/products" className="btn-outline-gold">Back to the catalogue</Link>
        </div>
      </div>
    );
  }

  // Full manifest for THIS product: styled images first, plain-background
  // original preserved as the last thumbnail option (never the main image).
  const images = manifest[product.code] || [];
  const inSets = sets.filter((s) => s.items.includes(product.code));

  // Related products — other items from the same sets, excluding this one
  const related = Array.from(
    new Set(inSets.flatMap((s) => s.items).filter((c) => c !== product.code))
  )
    .map((c) => products[c])
    .filter(Boolean)
    .slice(0, 4);

  const enquire = () => {
    setConfirm(true);
    setTimeout(() => setConfirm(false), 4500);
  };

  return (
    <div data-testid={`product-detail-${codeToSlug(product.code)}`}>
      {/* Crumb */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-10">
        <Link to="/products" className="inline-flex items-center gap-2 eyebrow-sm text-mb-muted hover:text-gold transition-colors">
          <ArrowLeft size={12} strokeWidth={1.25} /> The catalogue
        </Link>
      </div>

      {/* SPLIT HERO */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-10 pb-24">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          {/* Gallery */}
          <div className="md:col-span-7">
            <ImageGallery
              code={product.code}
              name={product.name}
              images={images}
              mainRatio="aspect-[4/5]"
            />
          </div>

          {/* Info */}
          <div className="md:col-span-5 flex flex-col">
            <div className="eyebrow text-gold">{product.category}</div>
            <h1
              className="mt-5 font-display text-5xl md:text-6xl text-mb leading-[0.95]"
              data-testid="product-name"
            >
              {product.name}
            </h1>
            <div className="mt-8">
              <GoldRule size="med" />
            </div>
            <p className="mt-8 font-display italic text-[18px] text-mb-muted leading-relaxed">
              {product.description}
            </p>
            {product.code && (
              <p className="mt-3 text-[11px] tracking-widest text-mb-muted/50 uppercase select-all">
                Product code: {product.code}
              </p>
            )}

            {/* Spec row */}
            <dl className="mt-10 hairline-t hairline-b border-subtle py-6 grid grid-cols-2 gap-x-6 gap-y-5">
              <div>
                <dt className="eyebrow-sm text-mb-muted">Category</dt>
                <dd className="mt-2 font-display text-[15px] text-mb">{product.category}</dd>
              </div>
              <div>
                <dt className="eyebrow-sm text-mb-muted">Dimensions</dt>
                <dd className="mt-2 font-display text-[15px] text-mb">{product.dimensions}</dd>
              </div>
              <div className="col-span-2">
                <dt className="eyebrow-sm text-mb-muted">Leather</dt>
                <dd className="mt-2 font-display text-[15px] text-mb">{product.leather}</dd>
              </div>
            </dl>

            {/* Colors */}
            {product.colors?.length > 0 && (
              <div className="mt-8">
                <div className="eyebrow-sm text-mb-muted mb-4">
                  Available in {product.colors.length === 1 ? '1 finish' : `${product.colors.length} finishes`}
                </div>
                <div className="flex flex-wrap gap-4">
                  {product.colors.map((c, i) => (
                    <div
                      key={c.ref || `${c.label}-${i}`}
                      className="flex items-center gap-3"
                      data-testid={`product-color-${(c.ref || c.label || i)
                        .toString()
                        .replace(/\s/g, '-')}`}
                    >
                      <span
                        className="w-7 h-7 hairline border-gold"
                        style={{ background: c.hex }}
                        aria-hidden="true"
                      />
                      <div className="leading-tight">
                        <div className="font-display text-[13px] text-mb">{c.label}</div>
                        {c.ref && (
                          <div className="eyebrow-sm text-mb-muted">{c.ref}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Features */}
            {product.features?.length > 0 && (
              <div className="mt-10">
                <div className="eyebrow-sm text-mb-muted mb-4">Features</div>
                <div className="flex flex-wrap gap-2">
                  {product.features.map((f, i) => (
                    <FeatureBadge key={`${product.code}-f-${i}`}>{f}</FeatureBadge>
                  ))}
                </div>
              </div>
            )}

            {/* Sets containing this */}
            {inSets.length > 0 && (
              <div className="mt-10 hairline-t border-subtle pt-6">
                <div className="eyebrow-sm text-mb-muted mb-4">Featured in</div>
                <div className="flex flex-wrap gap-x-6 gap-y-3">
                  {inSets.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/sets/${s.slug}`}
                      className="eyebrow text-mb hover:text-gold transition-colors"
                      data-testid={`product-in-set-link-${s.slug}`}
                    >
                      {s.name} →
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* CTA — pieces are sold as part of a set only, no solo purchase */}
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                className="btn-gold"
                onClick={enquire}
                data-testid="product-enquire-btn"
              >
                Enquire about this piece
                <ArrowUpRight size={14} strokeWidth={1.25} />
              </button>
            </div>

            {confirm && (
              <div
                className="mt-6 hairline border-gold p-5 bg-mb-surface"
                data-testid="product-confirm-msg"
              >
                <div className="eyebrow text-gold mb-2">Noted.</div>
                <p className="text-[14px] text-mb-muted">
                  An atelier representative will write within one business day.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="hairline-t border-subtle" data-testid="related-strip">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20">
            <div className="flex items-end justify-between mb-10">
              <div>
                <div className="eyebrow text-mb-muted mb-4">Carried alongside</div>
                <h2 className="font-display text-3xl md:text-4xl text-mb">From the same sets.</h2>
              </div>
              <Link to="/products" className="eyebrow text-gold hover:text-mb transition-colors hidden md:inline">
                All pieces →
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {related.map((r) => (
                <Link
                  key={r.code}
                  to={`/products/${codeToSlug(r.code)}`}
                  className="block bg-mb-surface hairline border-subtle p-4 hover:border-gold transition-colors group"
                  data-testid={`related-product-${codeToSlug(r.code)}`}
                >
                  <ImageGallery
                    code={r.code}
                    name={r.name}
                    images={(manifest[r.code] || []).filter((u) => u.includes('-clean'))}
                    mainRatio="aspect-[4/5]"
                  />
                  <div className="mt-4">
                    <div className="eyebrow-sm text-gold">{r.code}</div>
                    <div className="mt-2 font-display text-[18px] text-mb group-hover:text-gold transition-colors">
                      {r.name}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
